import React, { useEffect, useRef } from 'react';

interface ConfirmModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    confirmButtonClass?: string;
    isLoading?: boolean;
    /** Tighter layout and typography (e.g. secondary confirmations). */
    size?: 'default' | 'compact';
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    confirmButtonClass = 'bg-red-600 hover:bg-red-700 text-white',
    isLoading = false,
    size = 'default',
}) => {
    const modalRef = useRef<HTMLDivElement>(null);
    const confirmButtonRef = useRef<HTMLButtonElement>(null);

    // Close on escape key
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen && !isLoading) {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose, isLoading]);

    // Focus confirm button when modal opens
    useEffect(() => {
        if (isOpen && confirmButtonRef.current) {
            confirmButtonRef.current.focus();
        }
    }, [isOpen]);

    // Click outside to close
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (modalRef.current && !modalRef.current.contains(e.target as Node) && !isLoading) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen, onClose, isLoading]);

    if (!isOpen) return null;

    const isCompact = size === 'compact';
    const shellClass = isCompact
        ? 'max-w-[min(100%,26rem)] rounded-2xl'
        : 'max-w-[520px] rounded-[28px]';
    const contentPad = isCompact ? 'px-6 pt-6 pb-5' : 'px-8 pt-10 pb-8';
    const titleClass = isCompact
        ? 'text-[1.125rem] font-semibold leading-snug'
        : 'text-[28px] font-bold leading-tight';
    const messageClass = isCompact
        ? 'text-[14px] leading-relaxed'
        : 'text-[17px] leading-relaxed font-normal';
    const messageGap = isCompact ? 'mt-3' : 'mt-5';
    const actionsPad = isCompact ? 'px-6 py-4 gap-5' : 'px-8 py-6 gap-10';
    const cancelClass = isCompact
        ? 'text-[14px] font-semibold'
        : 'text-[17px] font-semibold';
    const confirmClass = isCompact
        ? 'inline-flex items-center justify-center px-5 py-2.5 rounded-full bg-black dark:bg-white text-[14px] font-semibold text-white dark:text-black hover:opacity-90 transition-all active:scale-[0.98] disabled:opacity-50 min-w-[6.5rem]'
        : 'inline-flex items-center justify-center px-10 py-4 rounded-full bg-black dark:bg-white text-[17px] font-bold text-white dark:text-black hover:opacity-90 transition-all active:scale-[0.98] disabled:opacity-50 min-w-[180px]';
    const spinnerClass = isCompact ? 'h-4 w-4' : 'h-5 w-5';

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
            {/* Backdrop */}
            <div 
                className="fixed inset-0 bg-black/40 backdrop-blur-[2px] transition-opacity duration-300" 
                onClick={!isLoading ? onClose : undefined}
            />

            {/* Modal Container */}
            <div
                ref={modalRef}
                className={`relative transform overflow-hidden bg-white dark:bg-neutral-900 shadow-[0_8px_30px_rgb(0,0,0,0.12)] transition-all w-full ${shellClass} animate-in fade-in zoom-in-95 duration-200`}
            >
                {/* Content */}
                <div className={contentPad}>
                    <h3 className={`${titleClass} text-slate-900 dark:text-white`} style={{ fontFamily: 'Arial, sans-serif' }}>
                        {title}
                    </h3>
                    <div className={messageGap}>
                        <p className={`${messageClass} text-slate-500 dark:text-neutral-400`} style={{ fontFamily: 'Arial, sans-serif' }}>
                            {message}
                        </p>
                    </div>
                </div>

                {/* Separator */}
                <div className="h-[1px] w-full bg-slate-100 dark:bg-neutral-800" />

                {/* Actions */}
                <div className={`flex items-center justify-end ${actionsPad}`}>
                    <button
                        type="button"
                        disabled={isLoading}
                        onClick={onClose}
                        className={`${cancelClass} text-slate-500 hover:text-slate-800 dark:text-neutral-400 dark:hover:text-neutral-200 transition-colors disabled:opacity-50`}
                        style={{ fontFamily: 'Arial, sans-serif' }}
                    >
                        {cancelText}
                    </button>
                    <button
                        ref={confirmButtonRef}
                        type="button"
                        disabled={isLoading}
                        onClick={onConfirm}
                        className={confirmClass}
                        style={{ fontFamily: 'Arial, sans-serif' }}
                    >
                        {isLoading ? (
                            <span className="flex items-center">
                                <svg className={`animate-spin -ml-1 mr-2 ${spinnerClass}`} fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                Processing...
                            </span>
                        ) : (
                            confirmText
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConfirmModal;
